import { io, Socket } from 'socket.io-client';
import { getSocketUrl } from '../../../config/environment';
import { getSession } from '../../auth/supabase-auth';

// Configuration for socket connection - using functions to avoid initialization order issues
const SOCKET_CONFIG = {
    TRANSPORTS: ['websocket', 'polling'] as const,
    TIMEOUT: 10000,
    AUTO_CONNECT: true,
    FORCE_NEW: false
};

// Lazy socket initialization to avoid circular dependency issues in production builds
let _socket: Socket | null = null;

function createSocket(): Socket {
    const socket = io(getSocketUrl(), {
        transports: [...SOCKET_CONFIG.TRANSPORTS],
        timeout: SOCKET_CONFIG.TIMEOUT,
        autoConnect: SOCKET_CONFIG.AUTO_CONNECT,
        forceNew: SOCKET_CONFIG.FORCE_NEW,
        withCredentials: true,
        // Send the Supabase JWT on every (re)connect so the server can
        // authenticate the connection. Function form => a fresh token is
        // fetched per attempt (handles token refresh & reconnects). Without
        // this the server rejects with "no auth token" and the client retries
        // in a loop, flooding the logs and losing live training progress.
        auth: (cb: (data: { token: string }) => void) => {
            getSession()
                .then((s) => cb({ token: s?.access_token ?? '' }))
                .catch(() => cb({ token: '' }));
        },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5
    });

    // Connection event handlers
    socket.on('connect', () => {
    });

    socket.on('disconnect', () => {
    });

    socket.on('connect_error', (error) => {
        console.error('SocketIO connection error:', error);
    });

    socket.on('reconnect', () => {
    });

    socket.on('reconnect_error', (error) => {
        console.error('SocketIO reconnection error:', error);
    });

    socket.on('reconnect_failed', () => {
        console.error('SocketIO failed to reconnect to training system');
    });

    return socket;
}

// Export getter function for lazy initialization
export function getSocket(): Socket {
    if (!_socket) {
        _socket = createSocket();
    }
    return _socket;
}

// For backward compatibility - use getter
export const socket: Socket = new Proxy({} as Socket, {
    get(_, prop) {
        return (getSocket() as unknown as Record<string | symbol, unknown>)[prop];
    }
});

export default socket;